// Integración con el SDK de Rebill (web components, Stencil). El componente
// `<rebill-checkout>` renderiza un checkout de suscripción EMBEBIDO usando solo
// el PUBLIC KEY (seguro para el front) + un `instantPlan` inline. No hace falta
// backend ni credenciales secretas.
//
// Este módulo:
//  1. Registra los custom elements del SDK una sola vez (`ensureRebillSDK`).
//  2. Construye el JSON del plan de prueba (`buildInstantPlan`).
//
// Docs: https://docs.rebill.com/sdk/checkout

import { REBILL_ENV } from '../config';

let registered = false;

/**
 * Registra los web components de Rebill (idempotente). Debe correr en el
 * browser antes de usar `<rebill-checkout>`.
 */
export async function ensureRebillSDK(): Promise<void> {
  if (registered || typeof window === 'undefined') return;
  registered = true;

  // Configura el entorno del SDK (host de la API). Opcional: por defecto usa
  // producción. Los assets (íconos/animaciones) se cargan del CDN de Rebill.
  // Importamos de 'rebill/config' (barrel completo); el entry '.' del paquete
  // apunta solo al componente y no expone `initializeRebillSDK`.
  const { initializeRebillSDK } = await import('rebill/config');
  initializeRebillSDK({ environment: REBILL_ENV, debug: REBILL_ENV !== 'production' });

  const { defineCustomElements } = await import('rebill/loader');
  await defineCustomElements(window);
}

// ─── instantPlan (plan recurrente definido inline) ─────────────────────────
// Forma tomada de los tipos del SDK (`rebill/dist/types/api/entities/sessions`).
// `type: 'unlimited'` = suscripción sin fin; `pricingMode: 'fixed'` = monto
// fijo; `frequency.period: 'month'` = mensual.

export interface InstantPlanParams {
  /** Monto del cobro recurrente, en la unidad de la moneda. Ej: 10000. */
  amount: number;
  /** Moneda. Ej: 'ARS'. */
  currency: string;
  /** Nombre visible del plan. */
  title: string;
}

export function buildInstantPlan({ amount, currency, title }: InstantPlanParams) {
  return {
    currency,
    amount,
    name: [{ language: 'es', text: title }],
    plan: {
      name: [{ language: 'es', text: title }],
      frequency: { period: 'month', count: 1 },
      type: 'unlimited',
      pricingMode: 'fixed',
      metadata: null,
    },
  };
}
